/**
 * URL utility functions for cleaning and normalising URLs
 */

/**
 * Cleans and normalises URLs to prevent encoding issues
 * @param url - The input URL to clean
 * @returns The cleaned and normalised URL
 */
export function cleanAndNormaliseUrl(url: string): string {
  let cleanedUrl = url;
  
  try {
    // Decode URL if it contains encoded characters
    if (cleanedUrl.includes('%')) {
      try {
        cleanedUrl = decodeURIComponent(cleanedUrl);
        console.log('cleanAndNormaliseUrl: Decoded URL:', cleanedUrl);
      } catch (e) {
        console.warn('Failed to decode URL:', url);
      }
    }
    
    // Remove double protocol issues
    cleanedUrl = cleanedUrl.replace(/^https?:\/\/https?:\/\//, 'https://');
    cleanedUrl = cleanedUrl.replace(/^https?:\/\/https%3A%2F%2F/, 'https://');
    
    // Handle the specific case where we have a hash fragment with an encoded URL
    if (cleanedUrl.startsWith('#')) {
      cleanedUrl = cleanedUrl.replace(/^#/, '');
      if (cleanedUrl.includes('%')) {
        try {
          cleanedUrl = decodeURIComponent(cleanedUrl);
          console.log('cleanAndNormaliseUrl: Decoded hash fragment URL:', cleanedUrl);
        } catch (e) {
          console.warn('Failed to decode hash fragment URL:', cleanedUrl);
        }
      }
    }
    
    // Ensure protocol is present
    if (!cleanedUrl.startsWith('http://') && !cleanedUrl.startsWith('https://')) {
      cleanedUrl = 'https://' + cleanedUrl;
    }
    
    // Normalise to base domain
    try {
      const urlObj = new URL(cleanedUrl);
      cleanedUrl = urlObj.origin;
      console.log('cleanAndNormaliseUrl: Normalised URL:', cleanedUrl);
    } catch (e) {
      console.warn('Failed to parse URL:', cleanedUrl);
    }
    
    return cleanedUrl;
  } catch (error) {
    console.error('Error cleaning URL:', error);
    return url; // Return original if cleaning fails
  }
}

/**
 * Safely encodes a URL for use in query parameters
 * @param url - The URL to encode
 * @returns The safely encoded URL
 */
export function safeUrlEncode(url: string): string {
  try {
    // Clean the URL first
    const cleanedUrl = cleanAndNormaliseUrl(url);
    return encodeURIComponent(cleanedUrl);
  } catch (error) {
    console.error('Error encoding URL:', error);
    return encodeURIComponent(url); // Fallback to original encoding
  }
}

/**
 * Safely decodes a URL from query parameters
 * @param encodedUrl - The encoded URL to decode
 * @returns The safely decoded URL
 */
export function safeUrlDecode(encodedUrl: string): string {
  try {
    let decodedUrl = decodeURIComponent(encodedUrl);
    
    // Handle double-encoded URLs
    if (decodedUrl.includes('%')) {
      try {
        decodedUrl = decodeURIComponent(decodedUrl);
      } catch (e) {
        console.warn('Failed to double-decode URL:', encodedUrl);
      }
    }
    
    return cleanAndNormaliseUrl(decodedUrl);
  } catch (error) {
    console.error('Error decoding URL:', error);
    return encodedUrl; // Return original if decoding fails
  }
}
